import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, MapPinIcon, SparklesIcon } from 'lucide-react';
import { LOCATIONS } from '../../lib/locations';
import { DESTINATIONS } from '../../lib/destinations';
interface DestinationCarouselProps {
  onChoose: (locationId: string) => void;
}
export function DestinationCarousel({
  onChoose
}: DestinationCarouselProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const active = DESTINATIONS[activeIndex];
  const location = LOCATIONS.find(item => item.id === active.locationId)!;
  useEffect(() => {
    const timer = window.setInterval(() => {
      setActiveIndex(current => (current + 1) % DESTINATIONS.length);
    }, 6500);
    return () => window.clearInterval(timer);
  }, []);
  function move(direction: number) {
    setActiveIndex(current => (current + direction + DESTINATIONS.length) % DESTINATIONS.length);
  }
  return <section className="overflow-hidden bg-[#e7f0ee] px-5 py-24 sm:px-8" id="destinations" data-id="element-192" data-mp="4lxvkvluvfjz">
      <div className="mx-auto max-w-7xl" data-id="element-193" data-mp="3ak3jl11g68a5">
        <div className="flex flex-col justify-between gap-5 sm:flex-row sm:items-end" data-id="element-194" data-mp="3fkr4r62zvuwg">
          <div className="max-w-2xl" data-id="element-195" data-mp="3kc9b69nsenot">
            <p className="text-xs font-800 uppercase tracking-[0.2em] text-[#788e73]" data-id="element-196" data-mp="3kdajw7hbyy3d">Explore the destinations</p>
            <h2 className="mt-4 font-display text-4xl font-500 leading-tight text-[#29433b] sm:text-5xl" data-id="element-197" data-mp="3oct5l5gz7d79">Five ways to gather beautifully.</h2>
            <p className="mt-4 text-lg leading-8 text-[#5d7169]" data-id="element-198" data-mp="2wm541sbe45t2">Take a closer look at the landscapes, local culture, and wellness moments that make each city distinctive.</p>
          </div>
          <div className="flex gap-2" data-id="element-199" data-mp="35n3d4p0or3ii">
            <CarouselButton label="Previous destination" onClick={() => move(-1)} data-id="element-200" data-mp="35o5zcjn2chs3"><ArrowLeftIcon className="h-5 w-5" data-id="element-201" data-mp="fy1a9nxdk4ln" /></CarouselButton>
            <CarouselButton label="Next destination" onClick={() => move(1)} data-id="element-202" data-mp="fzvnsh6bgufp"><ArrowRightIcon className="h-5 w-5" data-id="element-203" data-mp="kvue4ab7gmir" /></CarouselButton>
          </div>
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-[1.55fr_0.85fr]" data-id="element-204" data-mp="2gyz8nytm3ix0">
          <div className="relative min-h-[540px] overflow-hidden rounded-[2.25rem] bg-[#1e3d37] shadow-2xl" data-id="element-205" data-mp="2cyoz6c6m2ade">
            <AnimatePresence mode="wait" data-id="element-206" data-mp="2qygbm8hhbipa">
              <motion.div key={active.locationId} initial={{
              opacity: 0,
              scale: 1.035
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.7
            }} className="absolute inset-0" data-id="element-207" data-mp="2qxey7xczqk4e">
                <img src={active.image} alt={`${location.city} landscape`} className="h-full w-full object-cover" data-id="element-208" data-mp="1y7g61t3i26vi" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#18352f]/90 via-[#18352f]/22 to-transparent" data-id="element-209" data-mp="27zc4j59hzz09" />
              </motion.div>
            </AnimatePresence>
            <div className="relative flex min-h-[540px] flex-col justify-end p-6 sm:p-10" data-id="element-210" data-mp="237axpyv3n0hm">
              <div className="mb-3 flex items-center gap-2 text-[#f3d78f]" data-id="element-211" data-mp="1fhghr8fhgvt5"><MapPinIcon className="h-4 w-4" data-id="element-212" data-mp="1akgboximvt21" /><span className="text-xs font-800 uppercase tracking-[0.17em]" data-id="element-213" data-mp="1ajf26xh9nemf">{location.province}</span></div>
              <h3 className="font-display text-5xl font-500 text-white sm:text-6xl" data-id="element-214" data-mp="3m5z0yn0ipaxk">{location.city}</h3>
              <p className="mt-4 max-w-xl text-lg leading-7 text-white/85" data-id="element-215" data-mp="3qzbgm7n7y7e6">{active.description}</p>
              <button onClick={() => onChoose(location.id)} className="mt-7 inline-flex w-fit items-center gap-2 rounded-full bg-[#d9b45d] px-5 py-3 font-700 text-[#234038] transition hover:bg-[#f0d486]" data-id="element-216" data-mp="3d8ga320702dr">Choose {location.city} <ArrowRightIcon className="h-4 w-4" data-id="element-217" data-mp="8qklhxr0nr3s" /></button>
            </div>
          </div>

          <AnimatePresence mode="wait" data-id="element-218" data-mp="dpdylp2e5g3f">
            <motion.aside key={`detail-${active.locationId}`} initial={{
            opacity: 0,
            x: 16
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -16
          }} transition={{
            duration: 0.35
          }} className="rounded-[2.25rem] bg-[#fbfaf6] p-6 shadow-sm sm:p-8" data-id="element-219" data-mp="3vxmqgidikhwe">
              <img src={active.spaImage} alt={`${location.city} wellness experience`} className="h-40 w-full rounded-2xl object-cover" data-id="element-220" data-mp="2kk0zcro3g688" />
              <DetailGroup title="Local highlights" items={active.landmarks} data-id="element-221" data-mp="34r7lyco3gs2j" />
              <DetailGroup title="Wellness" items={active.spas} icon={<SparklesIcon className="h-4 w-4 text-[#b68c37]" data-id="element-223" data-mp="2zz63ua48w74l" />} data-id="element-222" data-mp="2zzxy9hm3vyta" />
              <div className="mt-7 border-t border-[#e1e7dc] pt-6" data-id="element-224" data-mp="2v0czp8aulsh3"><p className="text-xs font-800 uppercase tracking-[0.15em] text-[#788e73]" data-id="element-225" data-mp="2v14mpuqdqhvr">Perfect for</p><div className="mt-3 flex flex-wrap gap-2" data-id="element-226" data-mp="2r0ubo3nqd1a1">{active.perfectFor.map(item => <span key={item} className="rounded-full bg-[#e8f0e7] px-3 py-1.5 text-sm font-700 text-[#496552]" data-id="element-227" data-mp="3noh7q0gw6hmy">{item}</span>)}</div></div>
            </motion.aside>
          </AnimatePresence>
        </div>

        <div className="mt-6 flex flex-wrap gap-2" role="tablist" aria-label="Retreat destinations" data-id="element-228" data-mp="3enizt73c1pz3">
          {DESTINATIONS.map((destination, index) => {
          const destinationLocation = LOCATIONS.find(item => item.id === destination.locationId)!;
          return <button key={destination.locationId} onClick={() => setActiveIndex(index)} role="tab" aria-selected={index === activeIndex} className={`rounded-full px-4 py-2 text-sm font-700 transition ${index === activeIndex ? 'bg-[#315146] text-white shadow-sm' : 'bg-white/70 text-[#5a7066] hover:bg-white'}`} data-id="element-229" data-mp="1ya2mhq531cw7" data-mpi={index}>{destinationLocation.emoji} {destinationLocation.city}</button>;
        })}
        </div>
      </div>
    </section>;
}
function DetailGroup({
  title,
  items,
  icon
}: {
  title: string;
  items: string[];
  icon?: React.ReactNode;
}) {
  return <div className="mt-7" data-id="element-230" data-mp="wij8emwjkr33"><p className="flex items-center gap-2 text-xs font-800 uppercase tracking-[0.15em] text-[#788e73]" data-id="element-231" data-mp="wl5eei3sayj7">{icon}{title}</p><ul className="mt-3 space-y-2" data-id="element-232" data-mp="16h8t0nftopn1">{items.map(item => <li key={item} className="flex items-center gap-2 text-sm font-600 text-[#344c43]" data-id="element-233" data-mp="16ijwem5cvvdm"><span className="h-1.5 w-1.5 rounded-full bg-[#d3ad58]" data-id="element-234" data-mp="1aj3t5om0owz8" />{item}</li>)}</ul></div>;
}
function CarouselButton({
  children,
  label,
  onClick
}: {
  children: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return <button onClick={onClick} aria-label={label} className="flex h-11 w-11 items-center justify-center rounded-full border border-[#bfcfc8] bg-white text-[#315146] transition hover:bg-[#315146] hover:text-white" data-id="element-235" data-mp="3cayq0y3m6wxe">{children}</button>;
}